// Opciones de menú (autenticacion.opciones). Sistemas → Gestión de Menús → Opciones.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, SELECT_PLACEHOLDER, Switch, auth, fieldCls } from './shared';

interface Opcion { id: string; submenu_id: string; nombre: string; ruta: string; icon: string | null; descripcion: string | null; orden: number; activo: boolean; submenus?: { nombre: string; menus?: { nombre: string } | null } | null }
interface SubmenuOpt { id: string; nombre: string; menus?: { nombre: string } | null }
const subLabel = (s?: { nombre: string; menus?: { nombre: string } | null } | null) => s ? `${s.menus?.nombre ?? '?'} › ${s.nombre}` : '—';
const vacio = { submenu_id: '', nombre: '', ruta: '', icon: '', descripcion: '', orden: 0, activo: true };

export function Opciones() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Opcion[]>({ queryKey: ['adm-opciones'], queryFn: () => auth.list<Opcion>('opciones', '?select=*,submenus(nombre,menus(nombre))&order=orden') });
  const { data: submenus = [] } = useQuery<SubmenuOpt[]>({ queryKey: ['adm-submenus-opt'], queryFn: () => auth.list<SubmenuOpt>('submenus', '?select=id,nombre,menus(nombre)&order=nombre') });
  const submenusOrdenados = useMemo(() => [...submenus].sort((a, b) => subLabel(a).localeCompare(subLabel(b))), [submenus]);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [fNombre, setFNombre] = useState('');
  const [fEstado, setFEstado] = useState('todos');
  const [fSubmenu, setFSubmenu] = useState('');
  const filtrados = useMemo(() => rows.filter((o) =>
    (!fNombre.trim() || o.nombre.toLowerCase().includes(fNombre.trim().toLowerCase())) &&
    (fEstado === 'todos' || (fEstado === 'activo' ? o.activo : !o.activo)) &&
    (!fSubmenu || o.submenu_id === fSubmenu)
  ), [rows, fNombre, fEstado, fSubmenu]);

  const abrir = (o?: Opcion) => {
    if (o) { setEditId(o.id); setForm({ submenu_id: o.submenu_id, nombre: o.nombre, ruta: o.ruta, icon: o.icon ?? '', descripcion: o.descripcion ?? '', orden: o.orden, activo: o.activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { submenu_id: form.submenu_id, nombre: form.nombre, ruta: form.ruta, icon: form.icon || null, descripcion: form.descripcion || null, orden: form.orden, activo: form.activo, updated_at: new Date().toISOString() };
      return editId ? auth.update('opciones', editId, body) : auth.create('opciones', body);
    },
    onSuccess: () => { toast.success('Opción guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['adm-opciones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => auth.remove('opciones', id),
    onSuccess: () => { toast.success('Opción eliminada'); void qc.invalidateQueries({ queryKey: ['adm-opciones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Opciones de menú</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva opción</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${fieldCls} w-64`} placeholder="Filtrar por nombre…" value={fNombre} onChange={(e) => setFNombre(e.target.value)} />
        <select className={`${fieldCls} w-40`} value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
          <option value="todos">Todos los estados</option><option value="activo">Activos</option><option value="inactivo">Inactivos</option>
        </select>
        <select className={`${fieldCls} w-56`} value={fSubmenu} onChange={(e) => setFSubmenu(e.target.value)}>
          <option value="">Todos los submenús</option>{submenusOrdenados.map((s) => <option key={s.id} value={s.id}>{subLabel(s)}</option>)}
        </select>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Opciones ({filtrados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Menú</th><th className="py-2 pr-3">Submenú</th><th className="py-2 pr-3">Ruta</th><th className="py-2 pr-3">Orden</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin opciones</td></tr>}
              {filtrados.map((o) => (
                <tr key={o.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{o.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{o.submenus?.menus?.nombre ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{o.submenus?.nombre ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{o.ruta}</td>
                  <td className="py-2 pr-3 font-mono">{o.orden}</td>
                  <td className="py-2 pr-3">{o.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(o)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${o.nombre}"?`, { peligro: true })) eliminar.mutate(o.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar opción' : 'Nueva opción'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Submenú (Menú › Submenú) *">
              <select className={fieldCls} value={form.submenu_id} onChange={(e) => setForm({ ...form, submenu_id: e.target.value })}>
                <option value="">{SELECT_PLACEHOLDER}</option>
                {submenusOrdenados.map((s) => <option key={s.id} value={s.id}>{subLabel(s)}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <Campo label="Ruta *"><input className={fieldCls} value={form.ruta} onChange={(e) => setForm({ ...form, ruta: e.target.value })} placeholder="/area/x" /></Campo>
              <Campo label="Icono (lucide)"><input className={fieldCls} value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Descripción"><textarea className={`${fieldCls} h-16`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || !form.ruta.trim() || !form.submenu_id} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
